<script>
  import Layout from '../components/Layout.svelte';
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { createCurrencyFormatter } from '../lib/currency.js';
  import { user } from '../stores/authStore';

  export let jobId = '';

  let report = null;
  let loading = true;
  let calculating = false;
  let error = null;
  let expandedCodes = {};
  let expandedItemsForCode = {};
  let expandedItems = {};
  let progressMessage = '';
  let itemsReviewed = 0;
  let cachedAt = null;
  let stagePercent = 0;
  let currentStage = '';
  let chartTooltip = null;
  let chartTooltipX = 0;
  let colTooltip = null;
  let colTooltipRect = null;

  const COL_TIPS = {
    budgeted: 'The planned cost for this item, set when the job was created.',
    committed: 'The total value of purchase orders for this item (pending + approved).',
    bills: 'The total of all vendor bills received for this item.',
    abr: 'Actual Budget Remaining: Budgeted minus Actual Bills. Negative means over budget.',
    ctc: 'Cost to Complete: Projected minus Actual Bills. How much more is expected.',
    projected: 'The expected final cost. If Final, equals Actual Bills. Otherwise, the highest of Budgeted, Committed, or Actual Bills.',
    committedVariance: 'Budgeted Cost minus Committed Cost. Positive means budget remains uncommitted; negative means committed cost exceeds budget.',
    projectedVariance: 'Budgeted Cost minus Projected Cost. Positive means under budget; negative means projected to exceed budget.',
    final: 'Whether this cost item is finalised. Final items have their projected cost locked to actual bills.',
  };

  function showColTip(e, key) {
    colTooltip = key;
    colTooltipRect = e.currentTarget.getBoundingClientRect();
  }

  function hideColTip() {
    colTooltip = null;
    colTooltipRect = null;
  }

  function handleChartHover(e, pts) {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    const idx = Math.round(pct * (pts.length - 1));
    const clamped = Math.max(0, Math.min(pts.length - 1, idx));
    chartTooltip = pts[clamped];
    chartTooltipX = (clamped / (pts.length - 1)) * 100;
  }

  function handleChartLeave() {
    chartTooltip = null;
  }

  $: formatCurrency = createCurrencyFormatter($user?.country);

  function formatDate(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-NZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  function timeAgo(isoStr) {
    if (!isoStr) return '';
    const diff = Date.now() - new Date(isoStr).getTime();
    if (diff < 0) return 'just now';
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} minute${mins !== 1 ? 's' : ''} ago`;
    if (hrs < 24) return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
    const remainHrs = hrs % 24;
    if (remainHrs === 0) return `${days} day${days !== 1 ? 's' : ''} ago`;
    return `${days} day${days !== 1 ? 's' : ''} and ${remainHrs} hour${remainHrs !== 1 ? 's' : ''} ago`;
  }

  function toggleCode(codeId) {
    expandedCodes[codeId] = !expandedCodes[codeId];
    expandedCodes = expandedCodes;
  }

  function toggleItemsForCode(codeId, items) {
    const wasExpanded = expandedItemsForCode[codeId];
    expandedItemsForCode[codeId] = !wasExpanded;
    if (wasExpanded && items) {
      for (const item of items) {
        delete expandedItems[item.id];
      }
      expandedItems = expandedItems;
    }
    expandedItemsForCode = expandedItemsForCode;
  }

  function toggleItem(itemId, codeId) {
    if (expandedItemsForCode[codeId]) return;
    expandedItems[itemId] = !expandedItems[itemId];
    expandedItems = expandedItems;
  }

  function applyReport(data) {
    report = data;
    cachedAt = data.cachedAt || null;
    expandedCodes = {};
    for (const cc of (data.costCodes || [])) {
      expandedCodes[cc.id] = true;
    }
  }

  async function loadCachedReport() {
    try {
      const res = await fetch(`/api/jt/cost-reporting/jobs/${jobId}/cached-report`);
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          applyReport(data);
          loading = false;
          return true;
        }
      }
    } catch (e) {
      console.warn('Cache check failed, will calculate:', e);
    }
    return false;
  }

  function calculateReport() {
    calculating = true;
    error = null;
    progressMessage = 'Connecting to JobTread...';
    itemsReviewed = 0;
    stagePercent = 0;
    currentStage = '';

    const es = new EventSource(`/api/jt/cost-reporting/jobs/${jobId}/report`);

    es.addEventListener('progress', (e) => {
      const data = JSON.parse(e.data);
      progressMessage = data.message || '';
      if (data.itemsReviewed != null) itemsReviewed = data.itemsReviewed;
      if (data.stage) currentStage = data.stage;
      if (data.overallPercent != null) stagePercent = data.overallPercent;
    });

    es.addEventListener('complete', (e) => {
      es.close();
      applyReport(JSON.parse(e.data));
      loading = false;
      calculating = false;
    });

    es.addEventListener('error', (e) => {
      es.close();
      try {
        const data = JSON.parse(e.data);
        error = data.error || 'Failed to fetch cost report';
      } catch {
        error = 'Connection lost while fetching report. Please try again.';
      }
      loading = false;
      calculating = false;
    });

    es.onerror = () => {
      es.close();
      if (calculating) {
        error = 'Connection lost while fetching report. Please try again.';
        loading = false;
        calculating = false;
      }
    };
  }

  function recalculate() {
    loading = true;
    report = null;
    cachedAt = null;
    calculateReport();
  }

  function rawCurrency(amount) {
    if (amount == null || isNaN(amount)) return '0.00';
    return Number(amount).toFixed(2);
  }

  function csvEscape(val) {
    const s = String(val ?? '');
    if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
    return s;
  }

  function exportCSV() {
    if (!report) return;
    const rows = [];
    const jobName = report.job.name || 'report';

    rows.push(['Detailed Cost Report']);
    rows.push(['Job Name', report.job.name, 'Location', report.job.location || '-', 'Status', report.job.status]);
    rows.push([]);

    const hdr = ['Cost Code', 'Cost Item Name', 'Budgeted Cost', 'Committed Cost', 'Actual Bills', 'Actual Budget Remaining', 'Cost to Complete', 'Projected Cost', 'Committed Variance', 'Projected Variance', 'Final'];
    rows.push(hdr);
    rows.push([
      '', 'PROJECT TOTALS',
      rawCurrency(report.totals.totalBudgeted),
      rawCurrency(report.totals.totalCommitted),
      rawCurrency(report.totals.totalBills),
      rawCurrency(report.totals.totalActualBudgetRemaining),
      rawCurrency(report.totals.totalCostToComplete),
      rawCurrency(report.totals.totalProjected),
      rawCurrency(report.totals.totalVariance),
      rawCurrency(report.totals.totalProjectedVariance),
      ''
    ]);
    rows.push([]);

    for (const code of report.costCodes) {
      rows.push([
        code.number, code.name,
        rawCurrency(code.budgetedCost),
        rawCurrency(code.committedCost),
        rawCurrency(code.billsCost),
        rawCurrency(code.actualBudgetRemaining),
        rawCurrency(code.costToComplete),
        rawCurrency(code.projectedCost),
        rawCurrency(code.variance),
        rawCurrency(code.projectedVariance),
        code.isFinal ? 'Yes' : 'No'
      ]);

      for (const item of (code.costItems || [])) {
        rows.push([
          code.number, item.name,
          rawCurrency(item.budgetedCost),
          rawCurrency(item.committedCost),
          rawCurrency(item.billsCost),
          rawCurrency(item.actualBudgetRemaining),
          rawCurrency(item.costToComplete),
          rawCurrency(item.projectedCost),
          rawCurrency(item.variance),
          rawCurrency(item.projectedVariance),
          item.isFinal ? 'Yes' : 'No'
        ]);
      }
      rows.push([]);
    }

    const csvContent = rows.map(r => r.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${jobName.replace(/[^a-zA-Z0-9]/g, '_')}-cost-report.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function exportPDF() {
    if (!report) return;
    const { jsPDF } = await import('jspdf');
    const autoTableModule = await import('jspdf-autotable');
    const autoTable = autoTableModule.default || autoTableModule.autoTable;
    const jobName = report.job.name || 'report';
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const darkBlue = [0, 51, 102];
    const green = [21, 128, 61];
    const red = [220, 38, 38];
    let y = 15;

    doc.setFontSize(18);
    doc.setTextColor(...darkBlue);
    doc.text('Detailed Cost Report', 14, y);
    y += 8;

    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text(`Job Name: ${report.job.name}     Location: ${report.job.location || '-'}     Status: ${report.job.status}`, 14, y);
    y += 4;
    doc.text(`Generated: ${new Date().toLocaleDateString('en-NZ', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`, 14, y);
    y += 8;

    const summaryHeaders = [['', 'Budgeted', 'Committed', 'Actual Bills', 'ABR', 'CTC', 'Projected', 'Committed Var', 'Projected Var', 'Final']];
    const t = report.totals;
    const summaryBody = [[
      'PROJECT TOTALS',
      formatCurrency(t.totalBudgeted),
      formatCurrency(t.totalCommitted),
      formatCurrency(t.totalBills),
      formatCurrency(t.totalActualBudgetRemaining),
      formatCurrency(t.totalCostToComplete),
      formatCurrency(t.totalProjected),
      formatCurrency(t.totalVariance),
      formatCurrency(t.totalProjectedVariance),
      ''
    ]];

    autoTable(doc, {
      startY: y,
      head: summaryHeaders,
      body: summaryBody,
      theme: 'grid',
      headStyles: { fillColor: darkBlue, textColor: [255, 255, 255], fontSize: 7, halign: 'right', cellPadding: 2 },
      bodyStyles: { fontSize: 8, fontStyle: 'bold', halign: 'right', cellPadding: 2 },
      columnStyles: {
        0: { halign: 'left', fontStyle: 'bold' },
      },
      styles: { lineColor: [200, 200, 200], lineWidth: 0.2 },
      margin: { left: 14, right: 14 },
    });

    y = doc.lastAutoTable.finalY + 8;

    for (const code of report.costCodes) {
      if (y > doc.internal.pageSize.getHeight() - 40) {
        doc.addPage();
        y = 15;
      }

      doc.setFontSize(11);
      doc.setTextColor(...darkBlue);
      doc.text(`${code.number}  ${code.name}`, 14, y);
      y += 2;

      const codeHeaders = [['Budgeted', 'Committed', 'Actual Bills', 'ABR', 'CTC', 'Projected', 'Committed Var', 'Projected Var', 'Final']];
      const codeBody = [[
        formatCurrency(code.budgetedCost),
        formatCurrency(code.committedCost),
        formatCurrency(code.billsCost),
        formatCurrency(code.actualBudgetRemaining),
        formatCurrency(code.costToComplete),
        formatCurrency(code.projectedCost),
        formatCurrency(code.variance),
        formatCurrency(code.projectedVariance),
        code.isFinal ? 'Yes' : 'No'
      ]];

      autoTable(doc, {
        startY: y,
        head: codeHeaders,
        body: codeBody,
        theme: 'grid',
        headStyles: { fillColor: [240, 253, 244], textColor: darkBlue, fontSize: 6, halign: 'right', cellPadding: 1.5 },
        bodyStyles: { fontSize: 7, fontStyle: 'bold', halign: 'right', cellPadding: 1.5 },
        styles: { lineColor: [200, 200, 200], lineWidth: 0.2 },
        margin: { left: 14, right: 14 },
        didParseCell(data) {
          if (data.section === 'body') {
            if (data.column.index === 4) {
              data.cell.styles.textColor = code.actualBudgetRemaining < 0 ? red : green;
            }
            if (data.column.index === 6) data.cell.styles.textColor = code.variance < 0 ? red : green;
            if (data.column.index === 7) data.cell.styles.textColor = code.projectedVariance < 0 ? red : green;
          }
        }
      });

      y = doc.lastAutoTable.finalY + 2;

      const items = code.costItems || [];
      if (items.length > 0) {
        const itemHeaders = [['Cost Item', 'Budgeted', 'Committed', 'Actual Bills', 'ABR', 'CTC', 'Projected', 'Committed Var', 'Projected Var', 'Final']];
        const itemRows = items.map(ci => [
          ci.name,
          formatCurrency(ci.budgetedCost),
          formatCurrency(ci.committedCost),
          formatCurrency(ci.billsCost),
          formatCurrency(ci.actualBudgetRemaining),
          formatCurrency(ci.costToComplete),
          formatCurrency(ci.projectedCost),
          formatCurrency(ci.variance),
          formatCurrency(ci.projectedVariance),
          ci.isFinal ? 'Yes' : 'No'
        ]);

        autoTable(doc, {
          startY: y,
          head: itemHeaders,
          body: itemRows,
          theme: 'striped',
          headStyles: { fillColor: [236, 254, 255], textColor: [21, 94, 117], fontSize: 6, cellPadding: 1.5 },
          bodyStyles: { fontSize: 6.5, cellPadding: 1.5 },
          columnStyles: {
            1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' },
            4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' },
            7: { halign: 'right' }, 8: { halign: 'right' }, 9: { halign: 'right' }
          },
          styles: { lineColor: [220, 220, 220], lineWidth: 0.1 },
          margin: { left: 18, right: 14 },
          didParseCell(data) {
            if (data.section === 'body') {
              const ci = items[data.row.index];
              if (ci) {
                if (data.column.index === 4 && ci.actualBudgetRemaining < 0) data.cell.styles.textColor = red;
                if (data.column.index === 7) data.cell.styles.textColor = ci.variance < 0 ? red : green;
                if (data.column.index === 8) data.cell.styles.textColor = ci.projectedVariance < 0 ? red : green;
              }
            }
          }
        });

        y = doc.lastAutoTable.finalY + 6;
      } else {
        y += 6;
      }
    }

    const pageCount = doc.internal.getNumberOfPages();
    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.text(`Page ${i} of ${pageCount}`, pageW - 14, doc.internal.pageSize.getHeight() - 8, { align: 'right' });
    }

    doc.save(`${jobName.replace(/[^a-zA-Z0-9]/g, '_')}-cost-report.pdf`);
  }

  onMount(async () => {
    if (!jobId) return;
    const hasCached = await loadCachedReport();
    if (!hasCached) calculateReport();
  });
</script>

<Layout activeRoute="/cost-reporting">
  <div class="max-w-full mx-auto px-2 sm:px-4 lg:px-6 py-4">
    <!-- Back link -->
    <button
      on:click={() => navigate('/cost-reporting')}
      class="mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
    >
      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
      </svg>
      Back to Job List
    </button>

    {#if loading && calculating}
      <div class="flex flex-col items-center justify-center py-16 max-w-lg mx-auto">
        <div class="text-3xl font-bold text-[#2a3550] tabular-nums mb-6">
          {itemsReviewed.toLocaleString()} <span class="text-lg font-normal text-gray-500">items reviewed</span>
        </div>
        <div class="w-full bg-gray-200 rounded-full h-5 overflow-hidden mb-3">
          <div
            class="h-full rounded-full bg-gradient-to-r from-[#2a3550] to-[#5B8AC4] transition-all duration-300 ease-out progress-bar-animate"
            style="min-width: 2%; width: {Math.max(2, stagePercent)}%"
          ></div>
        </div>
        <p class="text-gray-500 text-sm mb-1">{progressMessage}</p>
        <p class="text-gray-400 text-xs">This can take several minutes on large jobs.</p>
      </div>
    {:else if loading}
      <div class="flex items-center justify-center py-12">
        <svg class="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span class="ml-3 text-gray-600">Loading report...</span>
      </div>
    {:else if error}
      <div class="bg-red-50 border border-red-200 rounded-lg p-4">
        <p class="text-red-800 text-sm">{error}</p>
        <button
          on:click={recalculate}
          class="mt-2 text-sm text-red-700 underline hover:text-red-900"
        >
          Try again
        </button>
      </div>
    {:else if report}
      <!-- Sticky Header (Dark Blue) -->
      <div class="sticky top-0 z-10 bg-[#2a3550] text-white p-4 sm:p-6 shadow-md rounded-t-lg">
        <div class="flex items-center justify-between mb-3">
          <h1 class="text-xl sm:text-2xl font-bold">Detailed Cost Report</h1>
          <div class="flex items-center gap-2">
            <button
              on:click={exportPDF}
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-white/15 hover:bg-white/25 transition-colors"
              title="Export as PDF"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
              </svg>
              PDF
            </button>
            <button
              on:click={exportCSV}
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-white/15 hover:bg-white/25 transition-colors"
              title="Export as CSV"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
              </svg>
              CSV
            </button>
          </div>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 text-xl">
          <div>
            <span class="font-semibold text-blue-200">Job Name:</span>
            <span class="ml-1">{report.job.name}</span>
          </div>
          <div>
            <span class="font-semibold text-blue-200">Location:</span>
            <span class="ml-1">{report.job.location || '-'}</span>
          </div>
          <div>
            <span class="font-semibold text-blue-200">Status:</span>
            <span class="ml-1 capitalize">{report.job.status}</span>
          </div>
        </div>
      </div>

      <!-- Budget vs Actuals Spark Chart -->
      {#if report.timeline && report.timeline.actuals.length > 1}
        {@const tl = report.timeline}
        {@const pts = tl.actuals}
        {@const proj = tl.projected || 0}
        {@const yMax = Math.max(tl.budget, proj, ...pts.map(p => p.cumulative)) * 1.1}
        {@const chartW = 800}
        {@const chartH = 120}
        {@const padL = 0}
        {@const padR = 0}
        {@const usableW = chartW - padL - padR}
        {@const xScale = usableW / (pts.length - 1)}
        {@const toY = (v) => chartH - (v / yMax) * chartH}
        {@const budgetY = toY(tl.budget)}
        {@const projY = toY(proj)}
        {@const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${padL + i * xScale},${toY(p.cumulative)}`).join(' ')}
        {@const areaPath = linePath + ` L${padL + (pts.length - 1) * xScale},${chartH} L${padL},${chartH} Z`}
        {@const crossIdx = pts.findIndex(p => p.cumulative > tl.budget)}
        <div class="bg-white border-b border-gray-200 px-4 sm:px-6 py-3">
          <div class="flex items-center justify-between mb-1">
            <span class="text-xs font-semibold uppercase tracking-wider text-gray-400">Budget vs Actuals</span>
            <div class="flex items-center gap-4 text-xs text-gray-500">
              <span class="flex items-center gap-1">
                <span class="inline-block w-3 h-0.5 bg-gray-400" style="border-top: 2px dashed #9ca3af;"></span>
                Budget ({formatCurrency(tl.budget)})
              </span>
              {#if proj > 0 && Math.abs(proj - tl.budget) > 1}
                <span class="flex items-center gap-1">
                  <span class="inline-block w-3 h-0.5 bg-orange-400" style="border-top: 2px dashed #f97316;"></span>
                  Projected ({formatCurrency(proj)})
                </span>
              {/if}
              <span class="flex items-center gap-1">
                <span class="inline-block w-3 h-2 rounded-sm bg-blue-400 opacity-60"></span>
                Actuals
              </span>
              {#if crossIdx >= 0}
                <span class="flex items-center gap-1">
                  <span class="inline-block w-3 h-2 rounded-sm bg-red-400 opacity-60"></span>
                  Over Budget
                </span>
              {/if}
            </div>
          </div>
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <div class="relative"
            on:mousemove={(e) => handleChartHover(e, pts)}
            on:mouseleave={handleChartLeave}
          >
            <svg viewBox="0 0 {chartW} {chartH}" preserveAspectRatio="none" class="w-full" style="height: 120px;">
              <defs>
                <clipPath id="aboveBudget">
                  <rect x="0" y="0" width={chartW} height={budgetY} />
                </clipPath>
                <clipPath id="belowBudget">
                  <rect x="0" y={budgetY} width={chartW} height={chartH - budgetY} />
                </clipPath>
              </defs>
              <!-- Area below budget line (blue) -->
              <path d={areaPath} fill="#3b82f6" opacity="0.15" clip-path="url(#belowBudget)" />
              <!-- Area above budget line (red) -->
              <path d={areaPath} fill="#ef4444" opacity="0.15" clip-path="url(#aboveBudget)" />
              <!-- Budget dashed line -->
              <line x1={padL} y1={budgetY} x2={chartW - padR} y2={budgetY}
                stroke="#9ca3af" stroke-width="1.5" stroke-dasharray="6,4" />
              <!-- Projected dashed line (orange, only if different from budget) -->
              {#if proj > 0 && Math.abs(proj - tl.budget) > 1}
                <line x1={padL} y1={projY} x2={chartW - padR} y2={projY}
                  stroke="#f97316" stroke-width="1.5" stroke-dasharray="6,4" />
              {/if}
              <!-- Actuals line below budget (blue) -->
              <path d={linePath} fill="none" stroke="#3b82f6" stroke-width="2" clip-path="url(#belowBudget)" />
              <!-- Actuals line above budget (red) -->
              <path d={linePath} fill="none" stroke="#ef4444" stroke-width="2" clip-path="url(#aboveBudget)" />
            </svg>
            {#if chartTooltip}
              <!-- Vertical indicator line -->
              <div class="absolute top-0 bottom-0 w-px bg-gray-400 pointer-events-none" style="left: {chartTooltipX}%;"></div>
              <!-- Dot on the line -->
              <div class="absolute w-2.5 h-2.5 rounded-full border-2 border-white pointer-events-none {chartTooltip.cumulative > tl.budget ? 'bg-red-500' : 'bg-blue-500'}"
                style="left: {chartTooltipX}%; top: {(1 - chartTooltip.cumulative / yMax) * 100}%; transform: translate(-50%, -50%);"></div>
              <!-- Tooltip -->
              <div class="absolute pointer-events-none z-20 chart-tooltip"
                style="left: {chartTooltipX}%; top: -8px; transform: translateX({chartTooltipX > 80 ? '-90%' : chartTooltipX < 20 ? '-10%' : '-50%'});">
                <div class="bg-[#2a3550] text-white text-xs rounded-md px-2.5 py-1.5 shadow-lg whitespace-nowrap">
                  <div class="font-semibold">{formatCurrency(chartTooltip.cumulative)}</div>
                  <div class="text-blue-200 text-[11px]">{chartTooltip.date}</div>
                </div>
              </div>
            {/if}
          </div>
          <div class="flex justify-between text-xs text-gray-400 mt-1">
            <span>{pts[0].date}</span>
            <span>{pts[pts.length - 1].date}</span>
          </div>
        </div>
      {/if}

      <!-- Cache Info Banner -->
      {#if cachedAt}
        <div class="bg-blue-50 border-b border-blue-200 px-4 sm:px-6 py-2.5 flex items-center justify-end gap-3">
          <span class="text-sm text-blue-800">
            Report generated {timeAgo(cachedAt)}
          </span>
          <button
            on:click={recalculate}
            disabled={calculating}
            class="inline-flex items-center px-5 py-2.5 text-base font-semibold rounded-lg text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-50 transition-colors shadow-sm"
          >
            {#if calculating}
              <svg class="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Recalculating...
            {:else}
              Recalculate Report with Latest Data
            {/if}
          </button>
        </div>
      {/if}

      <!-- Summary Totals Bar -->
      {@const gridCols = 'grid-cols-[auto_1fr_repeat(9,minmax(60px,1fr))]'}
      <div class="bg-white text-[#2a3550] px-4 sm:px-6 py-4 text-sm sm:text-base overflow-x-auto border-b border-gray-200">
        <div class="grid {gridCols} gap-1 min-w-[1250px] font-semibold text-[11px] uppercase tracking-wider text-[#2a3550]/70">
          <div class="w-6"></div>
          <div></div>
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <div class="text-right cursor-help" on:mouseenter={(e) => showColTip(e, 'budgeted')} on:mouseleave={hideColTip}>Budgeted Cost</div>
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <div class="text-right cursor-help" on:mouseenter={(e) => showColTip(e, 'committed')} on:mouseleave={hideColTip}>Committed Cost</div>
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <div class="text-right cursor-help" on:mouseenter={(e) => showColTip(e, 'bills')} on:mouseleave={hideColTip}>Actual Bills</div>
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <div class="text-right cursor-help" on:mouseenter={(e) => showColTip(e, 'abr')} on:mouseleave={hideColTip}>Actual Budget Remaining</div>
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <div class="text-right cursor-help" on:mouseenter={(e) => showColTip(e, 'ctc')} on:mouseleave={hideColTip}>Cost to Complete</div>
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <div class="text-right cursor-help" on:mouseenter={(e) => showColTip(e, 'projected')} on:mouseleave={hideColTip}>Projected Cost</div>
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <div class="text-right cursor-help" on:mouseenter={(e) => showColTip(e, 'committedVariance')} on:mouseleave={hideColTip}>Committed Variance</div>
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <div class="text-right cursor-help" on:mouseenter={(e) => showColTip(e, 'projectedVariance')} on:mouseleave={hideColTip}>Projected Variance</div>
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <div class="text-right cursor-help" on:mouseenter={(e) => showColTip(e, 'final')} on:mouseleave={hideColTip}>Final</div>
        </div>
        <div class="grid {gridCols} gap-1 min-w-[1250px] mt-1 font-bold text-sm">
          <div class="w-6"></div>
          <div>PROJECT TOTALS</div>
          <div class="text-right">{formatCurrency(report.totals.totalBudgeted)}</div>
          <div class="text-right">{formatCurrency(report.totals.totalCommitted)}</div>
          <div class="text-right">{formatCurrency(report.totals.totalBills)}</div>
          <div class="text-right">{formatCurrency(report.totals.totalActualBudgetRemaining)}</div>
          <div class="text-right">{formatCurrency(report.totals.totalCostToComplete)}</div>
          <div class="text-right">{formatCurrency(report.totals.totalProjected)}</div>
          <div class="text-right {report.totals.totalVariance < 0 ? 'text-red-600' : 'text-green-700'}">{formatCurrency(report.totals.totalVariance)}</div>
          <div class="text-right {report.totals.totalProjectedVariance < 0 ? 'text-red-600' : 'text-green-700'}">{formatCurrency(report.totals.totalProjectedVariance)}</div>
          <div class="text-right"></div>
        </div>
      </div>

      <!-- Cost Code Sections -->
      <div class="bg-white rounded-b-lg shadow-sm border border-gray-200 border-t-0 overflow-x-auto">
        {#each report.costCodes as code (code.id)}
          <!-- Cost Code Column Headers -->
          <div class="px-4 pt-4 pb-1 min-w-[1250px] border-t border-gray-200">
            <div class="grid {gridCols} gap-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              <div class="w-6"></div>
              <div></div>
              <!-- svelte-ignore a11y-no-static-element-interactions -->
              <div class="text-right cursor-help" on:mouseenter={(e) => showColTip(e, 'budgeted')} on:mouseleave={hideColTip}>Budgeted Cost</div>
              <!-- svelte-ignore a11y-no-static-element-interactions -->
              <div class="text-right cursor-help" on:mouseenter={(e) => showColTip(e, 'committed')} on:mouseleave={hideColTip}>Committed Cost</div>
              <!-- svelte-ignore a11y-no-static-element-interactions -->
              <div class="text-right cursor-help" on:mouseenter={(e) => showColTip(e, 'bills')} on:mouseleave={hideColTip}>Actual Bills</div>
              <!-- svelte-ignore a11y-no-static-element-interactions -->
              <div class="text-right cursor-help" on:mouseenter={(e) => showColTip(e, 'abr')} on:mouseleave={hideColTip}>Actual Budget Remaining</div>
              <!-- svelte-ignore a11y-no-static-element-interactions -->
              <div class="text-right cursor-help" on:mouseenter={(e) => showColTip(e, 'ctc')} on:mouseleave={hideColTip}>Cost to Complete</div>
              <!-- svelte-ignore a11y-no-static-element-interactions -->
              <div class="text-right cursor-help" on:mouseenter={(e) => showColTip(e, 'projected')} on:mouseleave={hideColTip}>Projected Cost</div>
              <!-- svelte-ignore a11y-no-static-element-interactions -->
              <div class="text-right cursor-help" on:mouseenter={(e) => showColTip(e, 'committedVariance')} on:mouseleave={hideColTip}>Committed Variance</div>
              <!-- svelte-ignore a11y-no-static-element-interactions -->
              <div class="text-right cursor-help" on:mouseenter={(e) => showColTip(e, 'projectedVariance')} on:mouseleave={hideColTip}>Projected Variance</div>
              <!-- svelte-ignore a11y-no-static-element-interactions -->
              <div class="text-right cursor-help" on:mouseenter={(e) => showColTip(e, 'final')} on:mouseleave={hideColTip}>Final</div>
            </div>
          </div>

          <!-- Cost Code Values Row (Green) -->
          <button
            class="w-full text-left bg-green-50 border-l-4 border-green-600 px-4 py-3 hover:bg-green-100 transition-colors cursor-pointer"
            on:click={() => toggleCode(code.id)}
          >
            <div class="grid {gridCols} gap-1 items-center min-w-[1250px] text-sm font-semibold">
              <div class="w-6 text-gray-500">
                <svg class="w-4 h-4 transition-transform {expandedCodes[code.id] ? 'rotate-90' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-green-800 font-mono">{code.number}</span>
                <span class="text-gray-900">{code.name}</span>
              </div>
              <div class="text-right text-gray-900">{formatCurrency(code.budgetedCost)}</div>
              <div class="text-right text-gray-900">{formatCurrency(code.committedCost)}</div>
              <div class="text-right text-gray-900">{formatCurrency(code.billsCost)}</div>
              <div class="text-right {code.actualBudgetRemaining < 0 ? 'text-red-600' : 'text-gray-900'}">
                {formatCurrency(code.actualBudgetRemaining)}
              </div>
              <div class="text-right text-gray-900">{formatCurrency(code.costToComplete)}</div>
              <div class="text-right text-gray-900">{formatCurrency(code.projectedCost)}</div>
              <div class="text-right {code.variance < 0 ? 'text-red-600' : 'text-green-700'}">{formatCurrency(code.variance)}</div>
              <div class="text-right {code.projectedVariance < 0 ? 'text-red-600' : 'text-green-700'}">
                {formatCurrency(code.projectedVariance)}
              </div>
              <div class="text-right text-gray-700 text-xs">
                {code.isFinal ? 'Yes' : 'No'}
              </div>
            </div>
          </button>

          {#if expandedCodes[code.id]}
            <!-- Expanded Cost Item Rows -->
            <!-- Expand all bills toggle -->
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <div
              class="border-l-4 border-transparent px-4 py-1 min-w-[1250px] cursor-pointer hover:bg-[#f0f4ff] transition-colors"
              on:click={() => toggleItemsForCode(code.id, code.costItems)}
            >
              <div class="flex items-center gap-1 ml-6 text-[11px] text-gray-400 font-medium uppercase tracking-wider select-none">
                <svg class="w-3 h-3 transition-transform {expandedItemsForCode[code.id] ? 'rotate-90' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
                {expandedItemsForCode[code.id] ? 'Hide' : 'Show'} all bill details
              </div>
            </div>

            {#each (code.costItems || []) as item (item.id)}
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <!-- svelte-ignore a11y-no-static-element-interactions -->
              <div
                class="border-l-4 border-transparent px-4 py-2 hover:bg-[#f0f4ff] transition-colors min-w-[1250px] {item.bills && item.bills.length > 0 && !expandedItemsForCode[code.id] ? 'cursor-pointer' : ''}"
                on:click={() => { if (item.bills && item.bills.length > 0) toggleItem(item.id, code.id); }}
              >
                <div class="grid {gridCols} gap-1 text-xs items-center">
                  <div class="w-6 text-gray-400">
                    {#if item.bills && item.bills.length > 0}
                      <svg class="w-3 h-3 transition-transform {(expandedItems[item.id] || expandedItemsForCode[code.id]) ? 'rotate-90' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                      </svg>
                    {/if}
                  </div>
                  <div class="text-gray-600 truncate" title={item.name}>{item.name}</div>
                  <div class="text-right text-gray-900">{formatCurrency(item.budgetedCost)}</div>
                  <div class="text-right text-gray-900">{formatCurrency(item.committedCost)}</div>
                  <div class="text-right text-gray-900">{formatCurrency(item.billsCost)}</div>
                  <div class="text-right {item.actualBudgetRemaining < 0 ? 'text-red-600' : 'text-gray-900'}">
                    {formatCurrency(item.actualBudgetRemaining)}
                  </div>
                  <div class="text-right text-gray-900">{formatCurrency(item.costToComplete)}</div>
                  <div class="text-right text-gray-900">{formatCurrency(item.projectedCost)}</div>
                  <div class="text-right {item.variance < 0 ? 'text-red-600' : 'text-green-700'}">{formatCurrency(item.variance)}</div>
                  <div class="text-right {item.projectedVariance < 0 ? 'text-red-600' : 'text-green-700'}">
                    {formatCurrency(item.projectedVariance)}
                  </div>
                  <div class="text-right">
                    <span class="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium
                      {item.isFinal ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}">
                      {item.isFinal ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Bill detail rows — spread across first columns, Gross Bill under Actual Bills -->
              {#if (expandedItems[item.id] || expandedItemsForCode[code.id]) && item.bills && item.bills.length > 0}
                <div class="bg-cyan-50 border-l-4 border-cyan-400 px-4 py-1 min-w-[1250px]">
                  <div class="grid {gridCols} gap-1 text-[11px] font-semibold uppercase tracking-wider text-cyan-800">
                    <div class="w-6"></div>
                    <div class="pl-4">Date / Vendor Name</div>
                    <div>Vendor Reference</div>
                    <div>Status</div>
                    <div class="text-right">Gross Bill</div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                  </div>
                </div>
                {#each item.bills as bill (bill.id + bill.vendorReference)}
                  <div class="border-l-4 border-cyan-100 px-4 py-1 hover:bg-blue-50/50 transition-colors min-w-[1250px]">
                    <div class="grid {gridCols} gap-1 text-[11px] items-center">
                      <div class="w-6"></div>
                      <div class="pl-4 text-gray-500 truncate">
                        <span>{formatDate(bill.date)}</span>
                        <span class="ml-2 text-gray-900">{bill.vendorName}</span>
                      </div>
                      <div class="text-gray-500 font-mono text-[11px] truncate">{bill.vendorReference}</div>
                      <div class="flex items-center gap-2">
                        <span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium
                          {bill.status === 'PAID' || bill.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                           bill.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                           'bg-gray-100 text-gray-700'}">
                          {bill.status}
                        </span>
                        <a
                          href="https://app.jobtread.com/jobs/{jobId}/documents/{bill.id}"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="text-blue-600 hover:text-blue-800 text-[11px] font-medium hover:underline"
                          on:click|stopPropagation
                        >Open in JT</a>
                      </div>
                      <div class="text-right text-gray-700">{formatCurrency(bill.grossBill)}</div>
                      <div></div>
                      <div></div>
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                  </div>
                {/each}
              {/if}
            {/each}

            {#if !code.costItems || code.costItems.length === 0}
              <div class="border-l-4 border-transparent px-4 py-3 text-sm text-gray-400 italic min-w-[1250px]">
                <div class="ml-6">No budget items for this cost code</div>
              </div>
            {/if}

            <!-- Totals Row -->
            <div class="border-l-4 border-green-300 px-4 py-2 bg-gray-50 border-b border-gray-200 min-w-[1250px]">
              <div class="grid {gridCols} gap-1 text-xs font-bold items-center">
                <div class="w-6"></div>
                <div class="text-gray-600">TOTAL</div>
                <div class="text-right text-gray-900">{formatCurrency(code.budgetedCost)}</div>
                <div class="text-right text-gray-900">{formatCurrency(code.committedCost)}</div>
                <div class="text-right text-gray-900">{formatCurrency(code.billsCost)}</div>
                <div class="text-right {code.actualBudgetRemaining < 0 ? 'text-red-600' : 'text-gray-900'}">
                  {formatCurrency(code.actualBudgetRemaining)}
                </div>
                <div class="text-right text-gray-900">{formatCurrency(code.costToComplete)}</div>
                <div class="text-right text-gray-900">{formatCurrency(code.projectedCost)}</div>
                <div class="text-right {code.variance < 0 ? 'text-red-600' : 'text-green-700'}">{formatCurrency(code.variance)}</div>
                <div class="text-right {code.projectedVariance < 0 ? 'text-red-600' : 'text-green-700'}">
                  {formatCurrency(code.projectedVariance)}
                </div>
                <div></div>
              </div>
            </div>
          {/if}
        {/each}

        {#if report.costCodes.length === 0}
          <div class="p-8 text-center text-gray-500">
            No cost codes found for this job's budget.
          </div>
        {/if}
      </div>

    {/if}
  </div>

  {#if colTooltip && colTooltipRect}
    <div
      class="fixed z-[100] pointer-events-none"
      style="top: {colTooltipRect.bottom + 6}px; left: {colTooltipRect.left + colTooltipRect.width / 2}px; transform: translateX(-50%);"
    >
      <div class="bg-[#2a3550] text-white text-xs rounded-lg shadow-xl px-3 py-2 max-w-[260px] leading-relaxed">
        <div class="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#2a3550] rotate-45"></div>
        {COL_TIPS[colTooltip]}
      </div>
    </div>
  {/if}
</Layout>

<style>
  :global(.rotate-90) {
    transform: rotate(90deg);
  }
  .progress-bar-animate {
    animation: pulse-bar 1.5s ease-in-out infinite;
  }
  @keyframes pulse-bar {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.75; }
  }
</style>
